'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type Status = 'loading' | 'form' | 'success' | 'error';

export function PasswordRecoveryPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string>('');
  const [formError, setFormError] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      const tokenHash = searchParams.get('token_hash');

      if (!tokenHash) {
        setError('Invalid reset link - no token provided');
        setStatus('error');
        return;
      }

      try {
        const { data, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'recovery',
        });

        if (verifyError) {
          console.error('Token verification error:', verifyError);
          setError('This reset link has expired or is invalid. Please request a new one.');
          setStatus('error');
          return;
        }

        if (data.session) {
          setStatus('form');
        } else {
          setError('Failed to verify reset link');
          setStatus('error');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError('Failed to verify reset link');
        setStatus('error');
      }
    };

    verifyToken();
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    // Validation
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        console.error('Password update error:', updateError);
        setFormError(updateError.message || 'Failed to update password');
        setIsSubmitting(false);
        return;
      }

      // Success!
      setStatus('success');

      // Try to redirect to app after delay
      setTimeout(() => {
        window.location.href = 'quappstore://auth/password-reset-success';
      }, 2000);

    } catch (err) {
      console.error('Update error:', err);
      setFormError('Failed to update password');
      setIsSubmitting(false);
    }
  };

  return (
    <main className="recovery-page" role="main">
      <article className="recovery-card" aria-live="polite">
        {/* Minimal Branding */}
        <header className="brand">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="brand-icon">
            <rect width="32" height="32" rx="8" fill="#FF9500"/>
            <path d="M16 8C13.79 8 12 9.79 12 12V14H11C10.45 14 10 14.45 10 15V23C10 23.55 10.45 24 11 24H21C21.55 24 22 23.55 22 23V15C22 14.45 21.55 14 21 14H20V12C20 9.79 18.21 8 16 8ZM16 10C17.1 10 18 10.9 18 12V14H14V12C14 10.9 14.9 10 16 10ZM16 17C17.1 17 18 17.9 18 19C18 20.1 17.1 21 16 21C14.9 21 14 20.1 14 19C14 17.9 14.9 17 16 17Z" fill="white"/>
          </svg>
          <span className="brand-text">Quapp - Password Reset</span>
        </header>

        {/* Loading State */}
        {status === 'loading' && (
          <section className="content" aria-label="Loading">
            <div className="spinner" aria-hidden="true" />
            <h1>Verifying reset link</h1>
            <p>Please wait a moment...</p>
          </section>
        )}

        {/* Form State */}
        {status === 'form' && (
          <section className="content form-content" aria-label="Password Reset Form">
            <h1>Create New Password</h1>
            <p className="subtitle">Your new password must be at least 6 characters</p>

            {formError && (
              <div className="form-error" role="alert">
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="form">
              <div className="input-group">
                <label htmlFor="password">New Password</label>
                <div className="input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    minLength={6}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="confirm-password">Confirm Password</label>
                <div className="input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    minLength={6}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting || password.length < 6}
              >
                {isSubmitting ? 'Updating...' : 'Reset Password'}
              </button>
            </form>
          </section>
        )}

        {/* Success State */}
        {status === 'success' && (
          <section className="content" aria-label="Success">
            <div className="icon success-icon" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="24" fill="#22c55e"/>
                <path d="M14 24L21 31L34 17" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>Password Reset!</h1>
            <p>Your password has been updated successfully.</p>
            <p className="hint">You can now sign in with your new password.</p>
            <a href="quappstore://auth/sign-in" className="button">
              Open App
            </a>
          </section>
        )}

        {/* Error State */}
        {status === 'error' && (
          <section className="content" aria-label="Error">
            <div className="icon error-icon" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="24" fill="#ef4444"/>
                <path d="M16 16L32 32M32 16L16 32" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <h1>Link Expired</h1>
            <p className="error-text">{error}</p>
            <p className="hint">Please request a new password reset link from the app.</p>
          </section>
        )}
      </article>

      <style>{`
        .recovery-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%);
        }

        .recovery-card {
          background: #ffffff;
          border-radius: 20px;
          padding: 40px 32px;
          max-width: 400px;
          width: 100%;
          text-align: center;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
        }

        .brand {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid #f1f1f1;
        }

        .brand-icon {
          flex-shrink: 0;
        }

        .brand-text {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a2e;
          letter-spacing: -0.3px;
        }

        .content {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .form-content {
          text-align: left;
          align-items: stretch;
        }

        .form-content h1 {
          text-align: center;
        }

        .spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #FF9500;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 24px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .icon {
          margin-bottom: 20px;
        }

        h1 {
          font-size: 22px;
          font-weight: 700;
          color: #1a1a2e;
          margin: 0 0 12px 0;
          letter-spacing: -0.5px;
        }

        p {
          font-size: 15px;
          color: #64748b;
          margin: 0 0 8px 0;
          line-height: 1.5;
        }

        .subtitle {
          text-align: center;
          margin-bottom: 24px;
        }

        .error-text {
          color: #dc2626;
          font-weight: 500;
        }

        .hint {
          font-size: 13px;
          color: #94a3b8;
          margin-top: 8px;
        }

        .form-error {
          background: #fef2f2;
          color: #dc2626;
          padding: 12px 16px;
          border-radius: 12px;
          font-size: 14px;
          margin-bottom: 20px;
          text-align: center;
        }

        .form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .input-group label {
          font-size: 14px;
          font-weight: 500;
          color: #374151;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-wrapper input {
          width: 100%;
          padding: 14px 48px 14px 16px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          font-size: 15px;
          color: #1a1a2e;
          background: #ffffff;
          transition: all 0.2s ease;
        }

        .input-wrapper input:focus {
          outline: none;
          border-color: #FF9500;
          box-shadow: 0 0 0 3px rgba(255, 149, 0, 0.1);
        }

        .input-wrapper input::placeholder {
          color: #9ca3af;
        }

        .toggle-password {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
        }

        .toggle-password:hover {
          color: #6b7280;
        }

        .submit-button {
          width: 100%;
          padding: 14px 24px;
          background: #FF9500;
          color: white;
          font-size: 15px;
          font-weight: 600;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 8px;
        }

        .submit-button:hover:not(:disabled) {
          background: #E68600;
          transform: translateY(-1px);
        }

        .submit-button:active:not(:disabled) {
          transform: translateY(0);
        }

        .submit-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .button {
          display: inline-block;
          margin-top: 24px;
          padding: 14px 32px;
          background: #FF9500;
          color: white;
          font-size: 15px;
          font-weight: 600;
          text-decoration: none;
          border-radius: 12px;
          transition: all 0.2s ease;
        }

        .button:hover {
          background: #E68600;
          transform: translateY(-1px);
        }

        .button:active {
          transform: translateY(0);
        }

        @media (max-width: 420px) {
          .recovery-card {
            padding: 32px 24px;
          }

          h1 {
            font-size: 20px;
          }
        }
      `}</style>
    </main>
  );
}

